import React from 'react';
import { GitHubTag } from '../types';

interface DualTagSelectorProps {
  tags: GitHubTag[];
  onTagSelection: (fromTag: GitHubTag | null, toTag: GitHubTag | null) => void;
  selectedFromTag: GitHubTag | null;
  selectedToTag: GitHubTag | null;
}

export const DualTagSelector: React.FC<DualTagSelectorProps> = ({
  tags,
  onTagSelection,
  selectedFromTag,
  selectedToTag,
}) => {
  if (!tags || tags.length === 0) {
    return (
      <div style={{ color: '#ff6b35', padding: '20px' }}>
        No tags found in the repository.
      </div>
    );
  }

  const handleToTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTag = tags.find(tag => tag.name === e.target.value);
    if (selectedTag) {
      validateAndSetTags(selectedFromTag, selectedTag);
    }
  };

  const handleFromTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTag = tags.find(tag => tag.name === e.target.value);
    if (selectedTag) {
      validateAndSetTags(selectedTag, selectedToTag);
    }
  };

  const validateAndSetTags = (fromTag: GitHubTag | null, toTag: GitHubTag | null) => {
    if (!fromTag || !toTag) {
      onTagSelection(fromTag, toTag);
      return;
    }

    const fromIndex = tags.findIndex(tag => tag.name === fromTag.name);
    const toIndex = tags.findIndex(tag => tag.name === toTag.name);

    if (fromIndex >= toIndex) {
      onTagSelection(fromTag, toTag);
    } else {
      // Invalid range - don't update
      onTagSelection(null, null);
    }
  };

  const isValidRange = () => {
    if (!selectedFromTag || !selectedToTag) return true;
    
    const fromIndex = tags.findIndex(tag => tag.name === selectedFromTag.name);
    const toIndex = tags.findIndex(tag => tag.name === selectedToTag.name);
    
    return fromIndex >= toIndex;
  };

  return (
    <div className="dual-tag-selector">
      <h3>📋 Select Tag Range</h3>
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Choose two versions to see all tickets/changes in newer version that aren't in baseline version
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label htmlFor="to-tag" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select newer version:
          </label>
          <select
            id="to-tag"
            value={selectedToTag?.name || ''}
            onChange={handleToTagChange}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value="">Choose a tag...</option>
            {tags.map(tag => (
              <option key={tag.name} value={tag.name}>
                {tag.name}
              </option>
            ))}
          </select>
          <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
            The newer version (commits up to this point will be included)
          </small>
        </div>

        <div>
          <label htmlFor="from-tag" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select baseline version:
          </label>
          <select
            id="from-tag"
            value={selectedFromTag?.name || ''}
            onChange={handleFromTagChange}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value="">Choose a tag...</option>
            {tags.map(tag => (
              <option key={tag.name} value={tag.name}>
                {tag.name}
              </option>
            ))}
          </select>
          <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
            The baseline version (commits after this point will be excluded)
          </small>
        </div>
      </div>

      {selectedFromTag && selectedToTag && !isValidRange() && (
        <div style={{ 
          color: '#ff6b35', 
          backgroundColor: '#fff5f0', 
          padding: '10px', 
          borderRadius: '4px', 
          border: '1px solid #ff6b35',
          marginBottom: '20px'
        }}>
          ⚠️ Invalid range: The older tag (right) must be older than the newer tag (left). Please adjust your selection.
        </div>
      )}
    </div>
  );
};
